import { ethers } from "ethers";
import {
  SHIELDED_POOL_JOINSPLIT_V2_INTERFACE,
  buildFlatJoinSplitV2Transaction,
  buildFlatMigrateV2Transaction,
  buildFlatPrivateBridgeV2Transaction,
  buildFlatPrivateSwapV2Transaction,
  buildFlatWithdrawBatchV2Transaction,
  buildFlatWithdrawV2Transaction,
  validateFlatJoinSplitV2WithdrawBatchEntries,
  computeFlatJoinSplitV2BridgeAuxHash,
  computeFlatJoinSplitV2SwapAuxHash,
  proveFlatJoinSplitV2,
  readFlatJoinSplitV2PoolSnapshot,
  resolveFlatJoinSplitV2Action,
  selectFlatJoinSplitV2Arity,
  simulateFlatJoinSplitV2Transaction,
  type FlatJoinSplitV2BridgeData,
  type FlatJoinSplitV2Hex32,
  type FlatJoinSplitV2HexData,
  type FlatJoinSplitV2InputNoteWitness,
  type FlatJoinSplitV2OutputNoteWitness,
  type FlatJoinSplitV2ProveRequest,
  type FlatJoinSplitV2SwapData,
} from "./sdk";
import { EVM_CONFIG } from "../config";
import {
  submitBasePoolRelay,
  serializeTransaction,
} from "../utils/relayer-client";
import { computeNPK, computeNullifier } from "../joinsplit/keys";
import type {
  JoinSplitNote,
  SpendingKeys,
  StoredJoinSplitNote,
} from "../joinsplit/types";
import {
  scanFlatJoinSplitV2Tree,
  type FlatJoinSplitV2MerklePath,
} from "./merkleIndex";
import {
  assertFlatJoinSplitV2Note,
  createFlatJoinSplitV2Note,
  flatFieldHex,
} from "./notes";
import { withFlatJoinSplitV2Prover } from "./prover";
import { assertAscendingLeafIndices, selectForAmount } from "./spendSelection";
import {
  markFlatJoinSplitV2SettlementBroadcast,
  markFlatJoinSplitV2SettlementFinalized,
  markFlatJoinSplitV2SettlementIncluded,
  prepareFlatJoinSplitV2SettlementJournal,
  type FlatJoinSplitV2JournalDelivery,
  type FlatJoinSplitV2JournalKind,
  type FlatJoinSplitV2JournalScope,
} from "./pendingJournal";
import { decimalScalar, fail } from "./common";

export async function validateInputs(params: {
  provider: ethers.Provider;
  pool: string;
  tokenAddress: string;
  spendingKeys: SpendingKeys;
  selected: StoredJoinSplitNote[];
  expectedTokenHash: string;
}): Promise<void> {
  assertAscendingLeafIndices(params.selected);
  const contract = new ethers.Contract(
    params.pool,
    ["function nullifierUsed(bytes32) view returns (bool)"],
    params.provider,
  );
  await Promise.all(
    params.selected.map(async ({ note }) => {
      await assertFlatJoinSplitV2Note({
        note,
        chainId: EVM_CONFIG.CHAIN_ID,
        poolAddress: params.pool,
        tokenAddress: params.tokenAddress,
        expectedTokenHash: params.expectedTokenHash,
        expectedMpk: params.spendingKeys.mpk,
      });
      const nullifier = await computeNullifier(
        params.spendingKeys.nullifyingKey,
        note.tokenHash,
        note.leafIndex,
      );
      if (await contract.nullifierUsed(nullifier)) {
        fail(`note ${note.commitment} is already spent on-chain`);
      }
    }),
  );
}

export function inputWitness(
  note: JoinSplitNote,
  path: FlatJoinSplitV2MerklePath,
  spendingKeys: SpendingKeys,
): FlatJoinSplitV2InputNoteWitness {
  if (path.leaf.toLowerCase() !== note.commitment.toLowerCase()) {
    fail("Merkle path leaf does not match the encrypted note commitment");
  }
  if (path.leafIndex !== note.leafIndex) {
    fail("encrypted leaf index disagrees with the authoritative event index");
  }
  return {
    npk: flatFieldHex(note.npk),
    token_hash: flatFieldHex(note.tokenHash),
    value: decimalScalar(note.value),
    random: flatFieldHex(note.random),
    leaf_index: decimalScalar(note.leafIndex),
    nullifying_key: flatFieldHex(spendingKeys.nullifyingKey),
    pub_key_x: flatFieldHex(spendingKeys.publicKey[0]),
    pub_key_y: flatFieldHex(spendingKeys.publicKey[1]),
    path_elements: path.pathElements as `0x${string}`[],
    path_indices: path.pathIndices,
  };
}

export function outputWitness(
  note: JoinSplitNote,
): FlatJoinSplitV2OutputNoteWitness {
  return {
    npk: flatFieldHex(note.npk),
    token_hash: flatFieldHex(note.tokenHash),
    value: decimalScalar(note.value),
  };
}

export function selfDeliveries(
  outputs: JoinSplitNote[],
  spendingKeys: SpendingKeys,
): FlatJoinSplitV2JournalDelivery[] {
  const recipientMpkHex = flatFieldHex(spendingKeys.mpk);
  return outputs.map((note) => ({
    commitment: note.commitment,
    recipientMpkHex,
    keepLocal: true,
  }));
}
